import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Offer, OfferStatusEnum } from "../../modules/shared/model/offer.interfaces";
import { createOffer as createOfferAction, updateOffer as updateOfferAction, fetchOffer as fetchOfferAction, fetchUserOffers as fetchUserOffersAction, deleteOffer as deleteOfferAction } from "../Actions/OfferActions";

// Async thunks
export const createOffer = createAsyncThunk(
  "offers/createOffer",
  async (offerData: Partial<Offer>, { rejectWithValue }) => {
    try {
      const response = await createOfferAction(offerData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateOffer = createAsyncThunk(
  "offers/updateOffer",
  async ({ id, data }: { id: string; data: Partial<Offer> }, { rejectWithValue }) => {
    try {
      const response = await updateOfferAction(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOffer = createAsyncThunk(
  "offers/fetchOffer",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await fetchOfferAction(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserOffers = createAsyncThunk(
  "offers/fetchUserOffers",
  async (params: { 
    page?: number; 
    limit?: number; 
    status?: OfferStatusEnum;
    search?: string;
    jobUserId?: string;
    isSent?: boolean;
    userId?: string;
    sort?: Array<{ orderBy: string; order: string }>;
    statusIds?: string;
    fromDate?: string;
    toDate?: string;
  }, { rejectWithValue }) => {
    try {
      const response = await fetchUserOffersAction(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteOffer = createAsyncThunk(
  "offers/deleteOffer",
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteOfferAction(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// State interface
interface OfferState {
  offers: Offer[];
  currentOffer: Offer | null;
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
}

// Initial state
const initialState: OfferState = {
  offers: [],
  currentOffer: null,
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
  hasNextPage: false,
};

// Slice
const offerSlice = createSlice({
  name: "offers",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentOffer: (state) => {
      state.currentOffer = null;
    },
    setCurrentOffer: (state, action: PayloadAction<Offer>) => {
      state.currentOffer = action.payload;
    },
    updateOfferInList: (state, action: PayloadAction<Offer>) => {
      const index = state.offers.findIndex(offer => offer.id === action.payload.id);
      if (index !== -1) {
        state.offers[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    // Create Offer
    builder
      .addCase(createOffer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOffer.fulfilled, (state, action) => {
        state.loading = false;
        state.offers.unshift(action.payload);
        state.totalCount += 1;
      })
      .addCase(createOffer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update Offer
    builder
      .addCase(updateOffer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOffer.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.offers.findIndex(offer => offer.id === action.payload.id);
        if (index !== -1) {
          state.offers[index] = action.payload;
        }
        if (state.currentOffer?.id === action.payload.id) {
          state.currentOffer = action.payload;
        }
      })
      .addCase(updateOffer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Offer
    builder
      .addCase(fetchOffer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOffer.fulfilled, (state, action) => {
        state.loading = false;
        console.log("Redux Slice Debug - Action Payload11111:", action.payload);
        state.currentOffer = action.payload;
      })
      .addCase(fetchOffer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch User Offers
    builder
      .addCase(fetchUserOffers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserOffers.fulfilled, (state, action) => {
        state.loading = false;
        console.log("Redux Slice Debug - Action Payload:", action.payload);
        state.offers = action.payload.data || [];
        state.totalCount = action.payload.totalCount || 0;
        state.currentPage = action.payload.currentPage || 1;
        state.totalPages = action.payload.totalPages || 1;
        state.hasNextPage = action.payload.hasNextPage || false;
      })
      .addCase(fetchUserOffers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete Offer
    builder
      .addCase(deleteOffer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOffer.fulfilled, (state, action) => {
        state.loading = false;
        state.offers = state.offers.filter(offer => offer.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
        if (state.currentOffer?.id === action.payload) {
          state.currentOffer = null;
        }
      })
      .addCase(deleteOffer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions and reducer
export const { clearError, clearCurrentOffer, setCurrentOffer, updateOfferInList } = offerSlice.actions;
export default offerSlice.reducer;
